import { getProvider } from './providers';
import {
  CONDITIONAL_PROPERTIES,
  NATURES,
  REQUIRED_PROPERTIES,
} from './taxonomy';

const IS_DEV = process.env.NODE_ENV !== 'production';

const extractNature = (eventName) => {
  const firstWord = eventName.split(' ')[0];

  return NATURES.includes(firstWord) ? firstWord : null;
};

const validateRequiredProperties = (nature, properties) => {
  const required = REQUIRED_PROPERTIES[nature];
  const errors = [];

  for (const [key, allowedValues] of Object.entries(required)) {
    if (!(key in properties)) {
      errors.push(`Missing required property "${key}" for "${nature}" event`);
      continue;
    }

    if (allowedValues !== null && !allowedValues.includes(properties[key])) {
      errors.push(
        `Invalid value "${properties[key]}" for "${key}". Allowed: ${allowedValues.join(', ')}`
      );
    }
  }

  return errors;
};

const validateConditionalProperties = (nature, properties) => {
  const conditionals = CONDITIONAL_PROPERTIES[nature];

  if (!conditionals) {
    return [];
  }

  const errors = [];

  for (const [key, rule] of Object.entries(conditionals)) {
    const conditionMet = properties[rule.when.field] === rule.when.value;

    if (conditionMet && !(key in properties)) {
      errors.push(
        `Missing "${key}" — required when ${rule.when.field} is "${rule.when.value}"`
      );
    }
  }

  return errors;
};

/**
 * Validated PostHog event tracking.
 * Only allowed way to fire PostHog events.
 *
 * Non-production: throws on validation errors.
 * Production: warns but still fires the event.
 *
 * @param {string} eventName - Name starting with a nature
 * @param {object} properties - Event properties
 */
const trackEvent = (eventName, properties = {}) => {
  const nature = extractNature(eventName);

  if (!nature) {
    const msg = `[PostHog] Event name must start with: ${NATURES.join(', ')}. Got: "${eventName}"`;

    if (IS_DEV) {
      throw new Error(msg);
    }

    // eslint-disable-next-line no-console
    console.warn(msg);
  }

  if (nature) {
    const requiredErrors = validateRequiredProperties(nature, properties);
    const conditionalErrors = validateConditionalProperties(nature, properties);
    const allErrors = [...requiredErrors, ...conditionalErrors];

    if (allErrors.length > 0) {
      const msg = `[PostHog] Validation errors for "${eventName}":\n${allErrors.join('\n')}`;

      if (IS_DEV) {
        throw new Error(msg);
      }

      // eslint-disable-next-line no-console
      console.warn(msg);
    }
  }

  // Intentional: in production, fire the event even after validation warnings.
  // Convention: never block user flows due to instrumentation errors. Invalid
  // events reach PostHog but are caught during dashboard analysis.
  const provider = getProvider();

  if (!provider) {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[PostHog] Event "${eventName}" dropped — SDK not initialized yet`
      );
    }
    return;
  }

  provider.track(eventName, properties);
};

export default trackEvent;
